/**
 * Selection System
 * 
 * Provides advanced selection tools for CAD:
 * - Window Selection (objects fully inside)
 * - Crossing Selection (objects touching or inside)
 * - Polygon Selection
 * - Lasso Selection
 * - Selection Filters
 */

import { create } from 'zustand';
import * as THREE from 'three';

// ============================================================================
// TYPES
// ============================================================================

export type SelectionMode =
    | 'click'     // Single click selection
    | 'window'    // Blue rectangle - only fully inside objects
    | 'crossing'  // Green rectangle - any touching objects
    | 'polygon'   // Custom polygon area
    | 'lasso';    // Freehand area

export type SelectionAction =
    | 'replace'   // Replace current selection
    | 'add'       // Add to selection (Shift)
    | 'remove'    // Remove from selection (Ctrl)
    | 'toggle';   // Toggle selection (Ctrl+Shift)

export interface SelectionFilter {
    entityTypes: string[];    // Filter by entity type
    layers: string[];         // Filter by layer
    locked: boolean;          // Include locked objects
    visible: boolean;         // Only visible objects
}

export interface SelectionBox {
    start: THREE.Vector2;     // Start corner (screen coords)
    end: THREE.Vector2;       // End corner (screen coords)
    mode: 'window' | 'crossing';
}

export interface SelectionState {
    mode: SelectionMode;
    action: SelectionAction;
    isSelecting: boolean;
    selectionBox: SelectionBox | null;
    polygonPoints: THREE.Vector2[];
    lassoPoints: THREE.Vector2[];
    filters: SelectionFilter;

    // Actions
    setMode: (mode: SelectionMode) => void;
    setAction: (action: SelectionAction) => void;
    startSelection: (point: THREE.Vector2, mode?: 'window' | 'crossing') => void;
    updateSelection: (point: THREE.Vector2) => void;
    endSelection: () => void;
    cancelSelection: () => void;
    addPolygonPoint: (point: THREE.Vector2) => void;
    addLassoPoint: (point: THREE.Vector2) => void;
    setFilter: (filter: Partial<SelectionFilter>) => void;
    resetFilters: () => void;
}

// ============================================================================
// DEFAULT FILTER
// ============================================================================

const defaultFilter: SelectionFilter = {
    entityTypes: [],  // Empty = all types
    layers: [],       // Empty = all layers
    locked: false,    // Don't select locked
    visible: true,    // Only visible
};

// ============================================================================
// SELECTION STORE
// ============================================================================

export const useSelectionStore = create<SelectionState>((set) => ({
    mode: 'click',
    action: 'replace',
    isSelecting: false,
    selectionBox: null,
    polygonPoints: [],
    lassoPoints: [],
    filters: { ...defaultFilter },

    setMode: (mode) => set({ mode }),
    setAction: (action) => set({ action }),

    startSelection: (point, mode) => set((_state) => {
        // Auto-detect mode based on drag direction
        const boxMode = mode || 'window';
        return {
            isSelecting: true,
            selectionBox: {
                start: point.clone(),
                end: point.clone(),
                mode: boxMode,
            },
        };
    }),

    updateSelection: (point) => set((state) => {
        if (!state.selectionBox) return state;

        // Auto-detect window vs crossing based on drag direction
        // Left-to-right = window (blue), Right-to-left = crossing (green)
        const isLeftToRight = point.x >= state.selectionBox.start.x;
        const mode = isLeftToRight ? 'window' : 'crossing';

        return {
            selectionBox: {
                ...state.selectionBox,
                end: point.clone(),
                mode,
            },
        };
    }),

    endSelection: () => set({
        isSelecting: false,
    }),

    cancelSelection: () => set({
        isSelecting: false,
        selectionBox: null,
        polygonPoints: [],
        lassoPoints: [],
    }),

    addPolygonPoint: (point) => set((state) => ({
        polygonPoints: [...state.polygonPoints, point.clone()],
    })),

    addLassoPoint: (point) => set((state) => ({
        lassoPoints: [...state.lassoPoints, point.clone()],
    })),

    setFilter: (filter) => set((state) => ({
        filters: { ...state.filters, ...filter },
    })),

    resetFilters: () => set({ filters: { ...defaultFilter } }),
}));

// ============================================================================
// SELECTION MANAGER
// ============================================================================

export class SelectionManager {
    private camera: THREE.Camera;
    private renderer: THREE.WebGLRenderer;
    // private scene: THREE.Scene;

    constructor(camera: THREE.Camera, renderer: THREE.WebGLRenderer, _scene: THREE.Scene) {
        this.camera = camera;
        this.renderer = renderer;
        // this.scene = scene;
    }

    updateCamera(camera: THREE.Camera): void {
        this.camera = camera;
    }

    /**
     * Find entities within a selection box (screen space)
     */
    findEntitiesInBox(
        box: SelectionBox,
        entities: Map<string, EntityBounds>,
        filters: SelectionFilter
    ): string[] {
        const { start, end, mode } = box;
        const selected: string[] = [];

        // Normalize box coordinates
        const minX = Math.min(start.x, end.x);
        const maxX = Math.max(start.x, end.x);
        const minY = Math.min(start.y, end.y);
        const maxY = Math.max(start.y, end.y);

        entities.forEach((bounds, id) => {
            // Apply filters
            if (!this.passesFilter(bounds, filters)) return;

            // Project entity bounding box to screen
            const screenBounds = this.projectBoundsToScreen(bounds.worldBounds);

            if (mode === 'window') {
                // Window selection: entity must be fully inside
                if (this.isFullyInside(screenBounds, minX, maxX, minY, maxY)) {
                    selected.push(id);
                }
            } else {
                // Crossing selection: entity must intersect
                if (this.intersects(screenBounds, minX, maxX, minY, maxY)) {
                    selected.push(id);
                }
            }
        });

        return selected;
    }

    /**
     * Find entities within a polygon (screen space)
     */
    findEntitiesInPolygon(
        polygon: THREE.Vector2[],
        entities: Map<string, EntityBounds>,
        filters: SelectionFilter
    ): string[] {
        if (polygon.length < 3) return [];

        const selected: string[] = [];

        entities.forEach((bounds, id) => {
            if (!this.passesFilter(bounds, filters)) return;

            // Check if entity center is inside polygon
            const center = this.getScreenCenter(bounds.worldBounds);
            if (this.pointInPolygon(center, polygon)) {
                selected.push(id);
            }
        });

        return selected;
    }

    /**
     * Check if entity passes filters
     */
    private passesFilter(bounds: EntityBounds, filters: SelectionFilter): boolean {
        // Visibility filter
        if (filters.visible && !bounds.visible) return false;

        // Locked filter
        if (!filters.locked && bounds.locked) return false;

        // Entity type filter
        if (filters.entityTypes.length > 0 && !filters.entityTypes.includes(bounds.type)) {
            return false;
        }

        // Layer filter
        if (filters.layers.length > 0 && !filters.layers.includes(bounds.layer)) {
            return false;
        }

        return true;
    }

    /**
     * Project 3D bounding box to 2D screen coordinates
     */
    private projectBoundsToScreen(worldBounds: THREE.Box3): ScreenBounds {
        const corners = [
            new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.min.z),
            new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.min.z),
            new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.max.z),
            new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.max.z),
            new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.min.z),
            new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.min.z),
            new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.max.z),
            new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.max.z),
        ];

        const size = this.renderer.getSize(new THREE.Vector2());
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;

        corners.forEach((corner) => {
            const projected = corner.clone().project(this.camera);
            const x = (projected.x * 0.5 + 0.5) * size.x;
            const y = (1 - (projected.y * 0.5 + 0.5)) * size.y;

            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
        });

        return { minX, maxX, minY, maxY };
    }

    /**
     * Get screen center of a 3D bounding box
     */
    private getScreenCenter(worldBounds: THREE.Box3): THREE.Vector2 {
        const center = new THREE.Vector3();
        worldBounds.getCenter(center);

        const projected = center.project(this.camera);
        const size = this.renderer.getSize(new THREE.Vector2());

        return new THREE.Vector2(
            (projected.x * 0.5 + 0.5) * size.x,
            (1 - (projected.y * 0.5 + 0.5)) * size.y
        );
    }

    /**
     * Check if screen bounds are fully inside selection box
     */
    private isFullyInside(
        bounds: ScreenBounds,
        minX: number, maxX: number, minY: number, maxY: number
    ): boolean {
        return bounds.minX >= minX && bounds.maxX <= maxX &&
            bounds.minY >= minY && bounds.maxY <= maxY;
    }

    /**
     * Check if screen bounds intersect selection box
     */
    private intersects(
        bounds: ScreenBounds,
        minX: number, maxX: number, minY: number, maxY: number
    ): boolean {
        return !(bounds.maxX < minX || bounds.minX > maxX ||
            bounds.maxY < minY || bounds.minY > maxY);
    }

    /**
     * Point in polygon test (ray casting algorithm)
     */
    private pointInPolygon(point: THREE.Vector2, polygon: THREE.Vector2[]): boolean {
        let inside = false;
        const n = polygon.length;

        for (let i = 0, j = n - 1; i < n; j = i++) {
            const xi = polygon[i].x, yi = polygon[i].y;
            const xj = polygon[j].x, yj = polygon[j].y;

            if (((yi > point.y) !== (yj > point.y)) &&
                (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)) {
                inside = !inside;
            }
        }

        return inside;
    }
}

// ============================================================================
// ENTITY BOUNDS TYPE
// ============================================================================

export interface EntityBounds {
    id: string;
    type: string;
    layer: string;
    visible: boolean;
    locked: boolean;
    worldBounds: THREE.Box3;
}

interface ScreenBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Determine selection action based on modifier keys
 */
export function getSelectionAction(event: MouseEvent | PointerEvent): SelectionAction {
    if (event.ctrlKey && event.shiftKey) return 'toggle';
    if (event.shiftKey) return 'add';
    if (event.ctrlKey) return 'remove';
    return 'replace';
}

/**
 * Apply selection action to current selection
 */
export function applySelectionAction(
    currentSelection: string[],
    newSelection: string[],
    action: SelectionAction
): string[] {
    switch (action) {
        case 'replace':
            return newSelection;
        case 'add':
            return [...new Set([...currentSelection, ...newSelection])];
        case 'remove':
            return currentSelection.filter(id => !newSelection.includes(id));
        case 'toggle': {
            const result = [...currentSelection];
            newSelection.forEach(id => {
                const index = result.indexOf(id);
                if (index >= 0) {
                    result.splice(index, 1);
                } else {
                    result.push(id);
                }
            });
            return result;
        }
        default:
            return newSelection;
    }
}
